import { ICompileService, IServiceProvider, Injectable } from "angular";
import { Dictionary, clone, startCase } from "lodash";
import { CellDoubleClickedEventHandler } from "../cells/doubleClickHandlerService";
import {
  booleanValueGetterFactory,
  dateTimeValueGetter,
  dateValueGetter,
  ValueGetter,
} from "../cells/valueGetters";
import { ColDef } from "./colDef";
import { FilterProvider } from "./filters/FilterProvider";

export const vsName = "columnDefinitionService";

export interface PermissionColDef {
  requiresPermission?: string | string[];
}

export interface simpleColDef extends PermissionColDef {
  field: string;
  header?: string;
  valueGetter?: ValueGetter;
  suppressMenu?: boolean;
  suppressSorting?: boolean;
  hide?: boolean;
  filter?: Function;
  cellDoubleClicked?: CellDoubleClickedEventHandler;
  cellRenderer?: awk.grid.ICellRenderer;
}

export interface commandColDef extends PermissionColDef {
  headerName: string;
  name: string;
  iconClass: string;
  width?: number;
  colId?: string;
  requiresPermission?: string;
  hide?: boolean;
}

export interface selectColDef extends PermissionColDef {
  headerName?: string;
}

export interface linkColDef extends PermissionColDef {
  width?: number;
  colId: string;
  iconClass: string;
  stateExpression: string;
  headerName: string;
}

export interface conditionalLinkColDef extends linkColDef{
  disableExpression: string;
}

export class ColumnDefinitionService {
  static $inject = ["$compile"];
  constructor(private readonly $compile: ICompileService) {}

  getSelectColDef(config?: selectColDef): ColDef {
    return {
      colId: "select",
      headerName: config?.headerName ?? "Select",
      width: 20,
      template: `
      <input
        type="checkbox"
        ng-click="recordGridController.toggleRowSelection(data)"
        ng-checked="recordGridController.rowSelected(data)"
      />`,
      suppressSorting: true,
      requiresPermission: config?.requiresPermission,
    };
  }

  getCommandColDef(config: commandColDef): ColDef {
    
    return {
      headerName: config.headerName,
      template: `<i ng-show="${config.name}" class="fa fa-lock text-danger" ng-click="$emit('command', {data, commandName:'${config.name}'})"></i>
      <i ng-show="!(${config.name})" class="${config.iconClass}" ng-click="$emit('command', {data, commandName:'${config.name}'})"></i>`,
      width: config.width ?? 20,
      colId: config.colId ?? config.headerName,
      requiresPermission: config.requiresPermission,
      hide: config.hide,
    };
  }

  getLinkColDef(config: linkColDef): ColDef {
    return {
      headerName: config.headerName,
      template: `<a ui-sref="${config.stateExpression}"><i class="${config.iconClass}"></i></a>`,
      colId: config.colId,
      width: config.width ?? 20,
      requiresPermission: config.requiresPermission,
    };
  }

  getConditionalLinkColDef(config: conditionalLinkColDef): ColDef{
    
    const colDef = this.getLinkColDef(config)
    colDef.template = `
    <a ng-show="${config.disableExpression}" style="color: grey"><i class="fa fa-pencil"></i></a>
    <a ng-hide="${config.disableExpression}" ui-sref="${config.stateExpression}"><i class="${config.iconClass}"></i></a>
    `

    return colDef;
  }

  getBooleanColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultBooleanFilters()
          .getGridFilterFactory(),
      booleanValueGetterFactory()
    );
  }

  getNumberColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultNumberFilters()
          .getGridFilterFactory()
    );
  }

  getStringColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultStringFilters()
          .getGridFilterFactory()
    );
  }

  getDateColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultDateFilters()
          .getGridFilterFactory(),
      dateValueGetter
    );
  }

  getDateTimeColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultDateFilters()
          .getGridFilterFactory(),
      dateTimeValueGetter
    );
  }

  simpleColDef(
    config: simpleColDef,
    filter?: Function,
    valueGetter?: ValueGetter
  ): ColDef {
    return {
      field: config.field,
      headerName: this.getHeader(config),
      filter,
      valueGetter: config.valueGetter ?? valueGetter,
      suppressMenu: config.suppressMenu,
      suppressSorting: config.suppressSorting,
      hide: config.hide,
      requiresPermission: config.requiresPermission,
      cellDoubleClicked: config.cellDoubleClicked,
      cellRenderer: config.cellRenderer,
    };
  }

  private getHeader(config: simpleColDef): string {
    return config.header ?? startCase(config.field);
  }
}
